import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import ManagerialCostForm from '@/components/Reports/ManagerialReports/ManagerialCostForm';
import { DefaultRouteTabs } from '@/components/_common';
import { useCostReport } from '@/core/domain/questReport/Reports/Cost/Cost.hook';
import {
  QuestCostFormValues,
  QuestCostRequest,
  QuestCostSituationType,
} from '@/core/domain/questReport/Reports/Cost/Cost.types';
import { QuestResponseType } from '@/core/domain/questReport/Reports/Quotation/Quotation.types';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import useReportGenerate, { ReportCallback } from '@/hooks/useReportGenerate';
import { PageProps } from '@/types/PageProps';
import { DateTimeFormat } from '@/utils/moment';
import { Divider, Form } from 'antd';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

export default function ManagerialCostPage({ route }: PageProps) {
  const [form] = Form.useForm<QuestCostFormValues>();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const report = useReportGenerate();

  const generateReport = useCostReport();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const { companiesIds, initialDate, finalDate, situation, ...values } =
        await form.validateFields();

      const obj: QuestCostRequest = {
        ...values,
        responseType:
          format === 'CSV' ? QuestResponseType.CSV : QuestResponseType.PDF,
        companiesIds: companiesIds?.toString(),
        situationOpen: situation?.includes(QuestCostSituationType.open),
        situationSettled: situation?.includes(
          QuestCostSituationType.liquidated,
        ),
        startDate: initialDate.format(DateTimeFormat),
        endDate: finalDate.format(DateTimeFormat),
      };

      const result = await generateReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'relatorio_custo.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport('PDF', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_custo.pdf',
      }),
    );
  };

  const handleCsv = () => {
    handleGenerateReport('CSV', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_custo.csv',
      }),
    );
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={useIntl().formatMessage({ id: route.name })} />

      <ManagerialCostForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}
